import type { Opportunity } from '../types/search';

const commonBenefits = [
  'Comprehensive medical, dental and vision coverage',
  'Executive retirement contribution',
  'Annual short-term incentive, subject to plan terms',
  'Relocation support where applicable, agreed at offer',
  'Executive health screening and coaching allowance',
];

export const opportunities: Opportunity[] = [
  {
    id: 'opp-1',
    referenceNumber: 'VEN-2026-0114',
    slug: 'chief-operating-officer-health-system',
    title: 'Chief Operating Officer',
    status: 'verified',
    lifecycle: 'open',
    clientDisclosure: 'disclosed',
    disclosedClientName: '[Client organization name — verified, publication approved]',
    clientDescriptor: 'A multi-site regional health system',
    mandateId: 'mandate-1',
    industry: 'Healthcare and Health Technology',
    function: 'Operations',
    location: 'Nashville, TN',
    region: 'united_states',
    workArrangement: 'onsite',
    employmentType: 'permanent',
    seniority: 'c_suite',
    compensation: {
      currency: 'USD',
      baseMin: 620000,
      baseMax: 760000,
      bonusTargetPct: 45,
      equityNote: 'Long-term incentive under the system’s executive plan.',
      isEstimate: false,
    },
    reportingLine: 'Reports to the Chief Executive Officer, with a standing report to the Board Quality Committee',
    roleOverview:
      'The Chief Operating Officer holds accountability for the operating performance of the system’s acute and ambulatory network. The appointee will own the operating plan, the capacity model and the relationship between clinical leadership and administrative delivery.',
    organizationalContext:
      'The system has grown through acquisition and is operating with materially different processes across its sites. A new operating model has been agreed at board level and requires an executive who has integrated networks of comparable complexity, rather than one who has run a single high-performing site.',
    keyResponsibilities: [
      'Own the system operating plan and its delivery against agreed quality, access and margin measures',
      'Lead the standardization of operating processes across acquired sites without degrading local clinical relationships',
      'Partner with the Chief Medical Officer on clinical governance and capacity decisions',
      'Hold accountability for the ambulatory expansion programme',
      'Report operating performance and risk to the Board Quality Committee',
      'Build the operating leadership bench beneath the executive committee',
    ],
    requiredQualifications: [
      'Operating leadership across multiple acute sites within one system',
      'Evidenced delivery of a standardization or integration programme following acquisition',
      'Experience partnering with a physician leadership structure that holds independent influence',
      'Board-committee reporting experience',
    ],
    preferredQualifications: [
      'Ambulatory network development experience',
      'Exposure to value-based care contracting',
      'Advanced degree in health administration, business or a clinical discipline',
    ],
    leadershipScope: {
      teamSize: 'Approximately 4,000 employees across the operating function',
      budget: 'Operating budget in the high hundreds of millions',
      pAndLOwnership: true,
      boardExposure: true,
      directReports: 'Seven direct reports, including site presidents',
    },
    benefits: commonBenefits,
    requiredSkills: [
      'Multi-site operating leadership',
      'Post-acquisition integration',
      'Clinical-administrative partnership',
      'Capacity and throughput management',
      'Board reporting',
    ],
    educationRequirement: 'Bachelor’s degree required; advanced degree preferred',
    workAuthorizationRequirement: ['us_citizen_or_permanent_resident', 'authorized_no_sponsorship_required'],
    screeningQuestions: [
      {
        id: 'q1',
        prompt:
          'Describe an integration or standardization programme you led across multiple sites. What changed measurably, and what did you have to abandon?',
        type: 'long_text',
        required: true,
      },
      {
        id: 'q2',
        prompt: 'Have you held accountability for operating performance across more than one acute site simultaneously?',
        type: 'boolean',
        required: true,
      },
      {
        id: 'q3',
        prompt: 'This role is onsite in Nashville, TN. Are you able to be based there?',
        type: 'single_select',
        options: ['Yes, already located there', 'Yes, willing to relocate', 'No'],
        required: true,
      },
    ],
    applicationDeadline: '2026-10-15',
    postedAt: '2026-08-18',
    updatedAt: '2026-08-27',
    ownerRecruiterId: 'rec-1',
    internal: {
      actualClientName: '[Internal: verified client legal entity name]',
      mandateOwnerUserId: 'user-rec-1',
      verificationEvidence:
        'Signed engagement letter dated 2026-08-04; board approval minute reference recorded; budget confirmed by system CFO.',
      verifiedByUserId: 'user-admin-1',
      verificationDate: '2026-08-15',
      vacancyStatus: 'confirmed_open',
      clientAuthorizationDate: '2026-08-16',
    },
  },
  {
    id: 'opp-2',
    referenceNumber: 'VEN-2026-0118',
    slug: 'chief-financial-officer-confidential-industrial',
    title: 'Chief Financial Officer',
    status: 'confidential_mandate',
    lifecycle: 'open',
    clientDisclosure: 'withheld_at_client_request',
    clientDescriptor: 'A privately held diversified industrial manufacturer with national operations',
    mandateId: 'mandate-2',
    industry: 'Manufacturing and Industrial',
    function: 'Finance',
    location: 'Chicago, IL',
    region: 'united_states',
    workArrangement: 'hybrid',
    employmentType: 'permanent',
    seniority: 'c_suite',
    compensation: {
      currency: 'USD',
      baseMin: 520000,
      baseMax: 640000,
      bonusTargetPct: 50,
      equityNote: 'Long-term incentive linked to enterprise value, structure disclosed at shortlist stage.',
      isEstimate: false,
    },
    reportingLine: 'Reports to the Chief Executive Officer, with direct access to the Audit Committee Chair',
    roleOverview:
      'The Chief Financial Officer will lead the finance function through a period of reporting modernization and prepare the organization for a wider set of capital options. The appointee will own financial reporting, treasury, planning and investor-readiness.',
    organizationalContext:
      'The organization has grown substantially while retaining finance processes designed for a smaller business. The board has agreed a modernization programme covering reporting quality, systems and the finance operating model. The incumbent remains in seat and the search is confidential.',
    keyResponsibilities: [
      'Own financial reporting, controls and the external audit relationship',
      'Lead the finance modernization programme, including systems and process',
      'Build planning and analysis capability that supports operating decision-making',
      'Manage treasury, banking relationships and the capital structure',
      'Prepare the organization for a broader set of capital options',
      'Report to the Audit Committee and support the Chair directly',
    ],
    requiredQualifications: [
      'Chief Financial Officer or divisional finance leadership in a business of comparable complexity',
      'Evidenced delivery of a finance transformation or systems modernization',
      'Audit-committee reporting experience',
      'Manufacturing or industrial sector experience',
    ],
    preferredQualifications: [
      'Experience preparing a private business for a transaction or public-market readiness',
      'Qualified accountant or equivalent',
      'Multi-entity, multi-jurisdiction consolidation experience',
    ],
    leadershipScope: {
      teamSize: 'Finance function of approximately 120',
      budget: 'Enterprise revenue in the low billions',
      pAndLOwnership: false,
      boardExposure: true,
      directReports: 'Six direct reports',
    },
    benefits: commonBenefits,
    requiredSkills: [
      'Financial reporting and controls',
      'Finance transformation',
      'Treasury and capital structure',
      'Financial planning and analysis',
      'Audit committee engagement',
    ],
    educationRequirement: 'Bachelor’s degree required; professional accounting qualification preferred',
    workAuthorizationRequirement: ['us_citizen_or_permanent_resident', 'authorized_no_sponsorship_required'],
    screeningQuestions: [
      {
        id: 'q1',
        prompt:
          'Describe a finance modernization programme you owned. What was the state of reporting before, and what specifically changed?',
        type: 'long_text',
        required: true,
      },
      {
        id: 'q2',
        prompt: 'Have you reported directly to an audit committee?',
        type: 'boolean',
        required: true,
      },
      {
        id: 'q3',
        prompt:
          'This search is confidential. Are you willing to proceed on the basis that the client’s identity is disclosed to you before your profile is presented, but not before?',
        type: 'single_select',
        options: ['Yes', 'No', 'I would like to discuss this first'],
        required: true,
      },
    ],
    applicationDeadline: '2026-10-31',
    postedAt: '2026-08-21',
    updatedAt: '2026-08-29',
    ownerRecruiterId: 'rec-1',
    internal: {
      actualClientName: '[Internal: verified client legal entity name — confidential, restricted access]',
      mandateOwnerUserId: 'user-rec-1',
      verificationEvidence:
        'Signed engagement letter dated 2026-08-12; confidentiality instruction recorded in writing by client General Counsel; incumbent in seat, not yet notified.',
      verifiedByUserId: 'user-admin-1',
      verificationDate: '2026-08-19',
      vacancyStatus: 'confirmed_open',
      clientAuthorizationDate: '2026-08-20',
    },
  },
  {
    id: 'opp-3',
    referenceNumber: 'VEN-2026-0121',
    slug: 'chief-information-security-officer',
    title: 'Chief Information Security Officer',
    status: 'verified',
    lifecycle: 'open',
    clientDisclosure: 'disclosed',
    disclosedClientName: '[Client organization name — verified, publication approved]',
    clientDescriptor: 'A publicly listed financial services institution',
    mandateId: 'mandate-3',
    industry: 'Cybersecurity',
    function: 'Information Security',
    location: 'New York, NY',
    region: 'united_states',
    workArrangement: 'hybrid',
    employmentType: 'permanent',
    seniority: 'svp',
    compensation: {
      currency: 'USD',
      baseMin: 450000,
      baseMax: 540000,
      bonusTargetPct: 60,
      equityNote: 'Restricted stock under the institution’s executive plan.',
      isEstimate: false,
    },
    reportingLine: 'Reports to the Chief Risk Officer, with a standing report to the Board Risk Committee',
    roleOverview:
      'A board-facing security leadership appointment. The appointee will own the institution’s security strategy, its regulatory engagement on operational resilience, and the quantified view of accepted exposure presented to the board.',
    organizationalContext:
      'Regulatory expectations on operational resilience have moved materially and the institution has concluded that its security function requires leadership with demonstrable regulatory engagement rather than technical depth alone. The role has been elevated to report into the Chief Risk Officer.',
    keyResponsibilities: [
      'Own the security strategy and the quantified view of accepted exposure',
      'Lead regulatory engagement on operational resilience and third-party risk',
      'Report to the Board Risk Committee on a standing basis',
      'Own incident response readiness and the disclosure decision framework',
      'Lead a global security function across engineering, operations and governance',
      'Partner with technology leadership on control design rather than control policing',
    ],
    requiredQualifications: [
      'Security leadership in a regulated financial institution',
      'Direct engagement with financial regulators on resilience or security matters',
      'Board or board-committee reporting experience',
      'Accountability for incident response at enterprise scale',
    ],
    preferredQualifications: [
      'Experience of a material incident and its regulatory aftermath',
      'Third-party and supply-chain risk programme ownership',
      'Relevant professional certification',
    ],
    leadershipScope: {
      teamSize: 'Security function of approximately 180',
      budget: 'Security budget in the high tens of millions',
      pAndLOwnership: false,
      boardExposure: true,
      directReports: 'Five direct reports',
    },
    benefits: commonBenefits,
    requiredSkills: [
      'Security strategy and risk quantification',
      'Regulatory engagement',
      'Incident response leadership',
      'Board reporting',
      'Third-party risk',
    ],
    educationRequirement: 'Bachelor’s degree required',
    licensingRequirement: 'No licence required. Relevant professional certification preferred but not mandatory.',
    workAuthorizationRequirement: ['us_citizen_or_permanent_resident', 'authorized_no_sponsorship_required'],
    screeningQuestions: [
      {
        id: 'q1',
        prompt:
          'Describe an occasion where you presented a quantified view of accepted risk to a board or board committee that they did not want to hear. What happened?',
        type: 'long_text',
        required: true,
      },
      {
        id: 'q2',
        prompt: 'Have you engaged directly with a financial regulator on security or resilience matters?',
        type: 'boolean',
        required: true,
      },
    ],
    applicationDeadline: '2026-10-08',
    postedAt: '2026-08-12',
    updatedAt: '2026-08-25',
    ownerRecruiterId: 'rec-2',
    internal: {
      actualClientName: '[Internal: verified client legal entity name]',
      mandateOwnerUserId: 'user-rec-2',
      verificationEvidence: 'Signed engagement letter dated 2026-08-01; role approved in institution’s headcount plan.',
      verifiedByUserId: 'user-admin-1',
      verificationDate: '2026-08-09',
      vacancyStatus: 'confirmed_open',
      clientAuthorizationDate: '2026-08-10',
    },
  },
  {
    id: 'opp-4',
    referenceNumber: 'VEN-2026-0126',
    slug: 'non-executive-director-audit-chair',
    title: 'Non-Executive Director and Audit Committee Chair',
    status: 'confidential_mandate',
    lifecycle: 'open',
    clientDisclosure: 'withheld_at_client_request',
    clientDescriptor: 'A private-equity backed technology business preparing for a public-market process',
    mandateId: 'mandate-4',
    industry: 'Technology, Data and Artificial Intelligence',
    function: 'Board and Governance',
    location: 'Boston, MA',
    region: 'united_states',
    workArrangement: 'hybrid',
    employmentType: 'board_appointment',
    seniority: 'board_director',
    compensation: {
      currency: 'USD',
      baseMin: 90000,
      baseMax: 130000,
      equityNote: 'Equity participation available; structure disclosed at shortlist stage.',
      isEstimate: false,
    },
    reportingLine: 'Appointed by the Board; chairs the Audit Committee and reports to the Board Chair',
    roleOverview:
      'An independent non-executive director appointment with the Audit Committee chair. The appointee will lead the committee through the strengthening of financial reporting and controls ahead of a public-market process.',
    organizationalContext:
      'The board is being professionalized ahead of a potential listing. It currently lacks an independent director with the financial expertise required to chair the Audit Committee under listed-company standards. The search is confidential because the transaction timetable is not public.',
    keyResponsibilities: [
      'Chair the Audit Committee and set its agenda and annual cycle',
      'Oversee the strengthening of financial reporting, internal control and audit readiness',
      'Lead the external auditor relationship on behalf of the board',
      'Contribute as an independent director to strategy and risk oversight',
      'Support the Chief Financial Officer through public-market readiness without crossing into management',
    ],
    requiredQualifications: [
      'Audit committee financial expertise as defined under listed-company standards',
      'Current or prior audit committee membership, ideally as chair',
      'Experience of a public-market listing process or a listed-company reporting environment',
      'Demonstrable independence from the sponsor and management',
    ],
    preferredQualifications: [
      'Technology-sector board experience',
      'Prior Chief Financial Officer or audit-partner background',
      'Capacity for the meeting cycle alongside existing commitments',
    ],
    leadershipScope: {
      pAndLOwnership: false,
      boardExposure: true,
      directReports: 'None. Committee chair with three committee members.',
    },
    benefits: ['Director fee and committee chair fee', 'Directors’ and officers’ liability cover', 'Reasonable expenses'],
    requiredSkills: [
      'Audit committee chairmanship',
      'Financial reporting oversight',
      'Internal control and audit readiness',
      'Public-market readiness',
      'Independent governance judgement',
    ],
    educationRequirement: 'Not specified. Professional accounting qualification advantageous.',
    workAuthorizationRequirement: ['us_citizen_or_permanent_resident', 'authorized_no_sponsorship_required'],
    screeningQuestions: [
      {
        id: 'q1',
        prompt: 'Please list your current directorships and committee roles, and confirm the time commitment each requires.',
        type: 'long_text',
        required: true,
      },
      {
        id: 'q2',
        prompt: 'Do you have any relationship with a private-equity sponsor or a technology business that might affect your independence?',
        type: 'long_text',
        required: true,
      },
      {
        id: 'q3',
        prompt: 'Have you chaired an audit committee?',
        type: 'boolean',
        required: true,
      },
    ],
    applicationDeadline: '2026-11-14',
    postedAt: '2026-08-25',
    updatedAt: '2026-08-30',
    ownerRecruiterId: 'rec-2',
    internal: {
      actualClientName: '[Internal: verified client legal entity name — confidential, restricted access]',
      mandateOwnerUserId: 'user-rec-2',
      verificationEvidence:
        'Nomination committee instruction minuted 2026-08-18; sponsor approval recorded; confidentiality instruction in writing.',
      verifiedByUserId: 'user-admin-1',
      verificationDate: '2026-08-23',
      vacancyStatus: 'confirmed_open',
      clientAuthorizationDate: '2026-08-24',
    },
  },
  {
    id: 'opp-5',
    referenceNumber: 'VEN-2026-0129',
    slug: 'chief-technology-officer-enterprise-software',
    title: 'Chief Technology Officer',
    status: 'verified',
    lifecycle: 'open',
    clientDisclosure: 'disclosed',
    disclosedClientName: '[Client organization name — verified, publication approved]',
    clientDescriptor: 'A private-equity backed enterprise software business',
    mandateId: 'mandate-5',
    industry: 'Technology, Data and Artificial Intelligence',
    function: 'Technology',
    location: 'Austin, TX',
    region: 'united_states',
    workArrangement: 'hybrid',
    employmentType: 'permanent',
    seniority: 'c_suite',
    compensation: {
      currency: 'USD',
      baseMin: 480000,
      baseMax: 580000,
      bonusTargetPct: 40,
      equityNote: 'Meaningful equity participation under the sponsor’s management incentive plan.',
      isEstimate: false,
    },
    reportingLine: 'Reports to the Chief Executive Officer',
    roleOverview:
      'The Chief Technology Officer will consolidate three acquired product platforms into a coherent architecture while maintaining delivery velocity, and will build the engineering leadership bench required for the next phase of growth.',
    organizationalContext:
      'The business has completed three acquisitions in two years and now operates three overlapping platforms with separate engineering organizations. The sponsor’s value-creation plan requires consolidation without a delivery pause, which is the central difficulty of the role.',
    keyResponsibilities: [
      'Define and deliver a consolidated platform architecture across three acquired products',
      'Maintain delivery commitments to existing customers throughout consolidation',
      'Build the engineering leadership layer and reduce single-person dependencies',
      'Own the applied machine-learning roadmap, including model governance and human review requirements',
      'Own engineering cost and infrastructure efficiency against the value-creation plan',
      'Contribute to diligence on further acquisitions',
    ],
    requiredQualifications: [
      'Engineering leadership at comparable or greater scale in enterprise software',
      'Evidenced platform consolidation following acquisition, without a delivery freeze',
      'Sponsor-backed operating experience',
      'Accountability for engineering cost as well as delivery',
    ],
    preferredQualifications: [
      'Applied machine-learning delivery in a production commercial setting',
      'Experience of an exit process',
      'Multi-geography engineering organization leadership',
    ],
    leadershipScope: {
      teamSize: 'Engineering organization of approximately 350 across three sites',
      budget: 'Engineering and infrastructure budget in the mid tens of millions',
      pAndLOwnership: false,
      boardExposure: true,
      directReports: 'Six direct reports',
    },
    benefits: commonBenefits,
    requiredSkills: [
      'Platform architecture consolidation',
      'Engineering organization leadership',
      'Applied machine learning delivery',
      'Cost and infrastructure efficiency',
      'Post-acquisition integration',
    ],
    educationRequirement: 'Bachelor’s degree required or equivalent professional experience',
    workAuthorizationRequirement: [
      'us_citizen_or_permanent_resident',
      'authorized_no_sponsorship_required',
      'sponsorship_required',
    ],
    screeningQuestions: [
      {
        id: 'q1',
        prompt:
          'Describe a platform consolidation you led across acquired products. How did you protect delivery commitments while doing it?',
        type: 'long_text',
        required: true,
      },
      {
        id: 'q2',
        prompt: 'Have you operated in a sponsor-backed business with an explicit value-creation plan?',
        type: 'boolean',
        required: true,
      },
    ],
    applicationDeadline: '2026-10-24',
    postedAt: '2026-08-27',
    updatedAt: '2026-08-31',
    ownerRecruiterId: 'rec-2',
    internal: {
      actualClientName: '[Internal: verified client legal entity name]',
      mandateOwnerUserId: 'user-rec-2',
      verificationEvidence: 'Signed engagement letter dated 2026-08-20; sponsor operating partner confirmed budget and mandate.',
      verifiedByUserId: 'user-admin-1',
      verificationDate: '2026-08-26',
      vacancyStatus: 'confirmed_open',
      clientAuthorizationDate: '2026-08-26',
    },
  },
  {
    id: 'opp-6',
    referenceNumber: 'VEN-2026-0131',
    slug: 'chief-human-resources-officer-proposed',
    title: 'Chief Human Resources Officer',
    status: 'proposed_opportunity',
    lifecycle: 'open',
    clientDisclosure: 'withheld_at_client_request',
    clientDescriptor: 'A mid-market professional services firm considering a first executive-level people appointment',
    industry: 'Professional Services',
    function: 'Human Resources',
    location: 'Atlanta, GA',
    region: 'united_states',
    workArrangement: 'hybrid',
    employmentType: 'permanent',
    seniority: 'c_suite',
    compensation: {
      currency: 'USD',
      baseMin: 330000,
      baseMax: 420000,
      bonusTargetPct: 30,
      isEstimate: true,
    },
    reportingLine: 'Expected to report to the Managing Partner',
    roleOverview:
      'A first executive-level people leadership appointment, covering partner and employee talent, total rewards and the organizational implications of the firm’s growth plan.',
    organizationalContext:
      'The firm has grown to a scale where partner-led people decisions no longer hold together, and is considering whether to create this seat. No mandate has been commissioned. This entry exists so that executives who would be interested can register that interest now rather than learn of the role after it is filled.',
    keyResponsibilities: [
      'Build the people function and operating model from a partner-led starting point',
      'Own partner and employee total rewards design',
      'Lead talent acquisition and development across the firm',
      'Advise the partnership on the organizational implications of the growth plan',
    ],
    requiredQualifications: [
      'Senior people leadership in a partnership or professional-services environment',
      'Total rewards design experience, including partner compensation exposure',
      'Experience building a function rather than inheriting one',
    ],
    preferredQualifications: ['Partnership governance exposure', 'Multi-office firm experience'],
    leadershipScope: {
      teamSize: 'Expected to build a function of approximately 20',
      pAndLOwnership: false,
      boardExposure: true,
      directReports: 'To be defined',
    },
    benefits: ['Benefits to be defined at the point a mandate is commissioned'],
    requiredSkills: [
      'People function build-out',
      'Total rewards design',
      'Partnership advisory',
      'Talent acquisition leadership',
    ],
    workAuthorizationRequirement: ['us_citizen_or_permanent_resident', 'authorized_no_sponsorship_required'],
    screeningQuestions: [
      {
        id: 'q1',
        prompt:
          'This is a proposed opportunity with no confirmed vacancy. Please confirm you understand that registering interest records consent to be considered only if a formal mandate is commissioned.',
        type: 'single_select',
        options: ['Understood'],
        required: true,
      },
      {
        id: 'q2',
        prompt: 'Describe your experience building a people function in a partnership environment.',
        type: 'long_text',
        required: true,
      },
    ],
    postedAt: '2026-08-29',
    updatedAt: '2026-08-29',
    ownerRecruiterId: 'rec-1',
    internal: {
      actualClientName: '[Internal: prospective client name — no engagement letter]',
      mandateOwnerUserId: 'user-rec-1',
      verificationEvidence:
        'Exploratory conversation with Managing Partner 2026-08-26. No engagement letter. No board or partnership approval. Vacancy not confirmed.',
      vacancyStatus: 'exploratory',
    },
  },
  {
    id: 'opp-7',
    referenceNumber: 'VEN-2026-0133',
    slug: 'vice-president-supply-chain-market-matched',
    title: 'Vice President, Supply Chain',
    status: 'market_matched_opportunity',
    lifecycle: 'open',
    clientDisclosure: 'withheld_at_client_request',
    clientDescriptor: 'Multiple consumer and industrial organizations within our research coverage',
    industry: 'Supply Chain and Logistics',
    function: 'Supply Chain',
    location: 'Multiple United States locations',
    region: 'united_states',
    workArrangement: 'hybrid',
    employmentType: 'permanent',
    seniority: 'vice_president',
    compensation: { currency: 'USD', baseMin: 280000, baseMax: 360000, bonusTargetPct: 25, isEstimate: true },
    reportingLine: 'Typically reports to a Chief Supply Chain Officer or Chief Operating Officer',
    roleOverview:
      'A role category identified through market research rather than a client instruction. Our research indicates recurring demand for supply-chain leaders with evidenced network redesign experience across consumer and industrial organizations.',
    organizationalContext:
      'This is not a vacancy. It is a documented role category from our market intelligence work. Registering interest places you in the relevant talent pool and records your consent to be approached if a mandate in this category is commissioned.',
    keyResponsibilities: [
      'Typical scope includes network design and planning',
      'Cost-to-serve analysis and improvement',
      'Distribution and transportation leadership',
      'Automation programme delivery',
    ],
    requiredQualifications: [
      'Senior supply-chain leadership with evidenced network redesign outcomes',
      'Multi-site distribution accountability',
    ],
    preferredQualifications: ['Warehouse automation programme experience', 'Consumer or industrial sector depth'],
    leadershipScope: { pAndLOwnership: false, boardExposure: false, directReports: 'Varies by organization' },
    benefits: ['Varies by organization'],
    requiredSkills: ['Network design', 'Cost-to-serve analysis', 'Distribution leadership', 'Automation delivery'],
    workAuthorizationRequirement: ['us_citizen_or_permanent_resident', 'authorized_no_sponsorship_required'],
    screeningQuestions: [
      {
        id: 'q1',
        prompt:
          'This is a market-matched role category, not a vacancy. Please confirm you understand that registering interest adds you to a talent pool and records consent to be approached if a mandate follows.',
        type: 'single_select',
        options: ['Understood'],
        required: true,
      },
      {
        id: 'q2',
        prompt: 'Describe a network redesign you led and the measured outcome.',
        type: 'long_text',
        required: true,
      },
    ],
    postedAt: '2026-08-30',
    updatedAt: '2026-08-30',
    ownerRecruiterId: 'rec-3',
    internal: {
      actualClientName: 'Not applicable — no client instruction',
      mandateOwnerUserId: 'user-rec-3',
      verificationEvidence:
        'Derived from Q3 2026 supply chain market map. No client engagement. Recorded as a research-derived talent pool.',
      vacancyStatus: 'anticipated',
    },
  },
  {
    id: 'opp-8',
    referenceNumber: 'VEN-2026-0136',
    slug: 'interim-chief-financial-officer-life-sciences',
    title: 'Interim Chief Financial Officer',
    status: 'verified',
    lifecycle: 'open',
    clientDisclosure: 'disclosed',
    disclosedClientName: '[Client organization name — verified, publication approved]',
    clientDescriptor: 'A clinical-stage biotechnology company',
    mandateId: 'mandate-6',
    industry: 'Life Sciences and Pharmaceuticals',
    function: 'Finance',
    location: 'Cambridge, MA',
    region: 'united_states',
    workArrangement: 'hybrid',
    employmentType: 'interim',
    seniority: 'c_suite',
    compensation: {
      currency: 'USD',
      baseMin: 0,
      baseMax: 0,
      equityNote: 'Day rate agreed at engagement. No equity participation for interim appointments.',
      isEstimate: false,
    },
    reportingLine: 'Reports to the Chief Executive Officer',
    roleOverview:
      'A defined nine-month interim appointment covering finance leadership through a financing round and the permanent search, with a documented handover to the permanent appointee.',
    organizationalContext:
      'The incumbent Chief Financial Officer departed at short notice during preparation for a financing round. The board requires experienced interim leadership to hold the seat and complete the round while a permanent search runs in parallel.',
    keyResponsibilities: [
      'Hold finance leadership through the financing round',
      'Own investor materials and diligence responses',
      'Maintain reporting, controls and cash management discipline',
      'Stabilize the finance team through the transition',
      'Deliver a documented handover to the permanent appointee',
    ],
    requiredQualifications: [
      'Chief Financial Officer experience in clinical-stage life sciences',
      'Completed at least one financing round in a similar setting',
      'Prior interim experience with evidenced handover discipline',
      'Immediate or near-immediate availability',
    ],
    preferredQualifications: ['Public-market reporting experience', 'Prior board-facing investor engagement'],
    leadershipScope: {
      teamSize: 'Finance team of approximately 12',
      pAndLOwnership: false,
      boardExposure: true,
      directReports: 'Three direct reports',
    },
    benefits: ['Day rate agreed at engagement', 'Reasonable expenses'],
    requiredSkills: ['Financing round execution', 'Investor materials', 'Cash management', 'Interim handover discipline'],
    educationRequirement: 'Bachelor’s degree required; professional accounting qualification preferred',
    workAuthorizationRequirement: ['us_citizen_or_permanent_resident', 'authorized_no_sponsorship_required'],
    screeningQuestions: [
      {
        id: 'q1',
        prompt: 'What is your earliest availability, and what is your current commitment position?',
        type: 'short_text',
        required: true,
      },
      {
        id: 'q2',
        prompt: 'Describe an interim finance appointment you held and how you handed over to the permanent appointee.',
        type: 'long_text',
        required: true,
      },
    ],
    applicationDeadline: '2026-09-26',
    postedAt: '2026-08-31',
    updatedAt: '2026-08-31',
    ownerRecruiterId: 'rec-3',
    internal: {
      actualClientName: '[Internal: verified client legal entity name]',
      mandateOwnerUserId: 'user-rec-3',
      verificationEvidence: 'Signed interim engagement letter dated 2026-08-28; board minute referencing the appointment recorded.',
      verifiedByUserId: 'user-admin-1',
      verificationDate: '2026-08-30',
      vacancyStatus: 'confirmed_open',
      clientAuthorizationDate: '2026-08-30',
    },
  },
  {
    id: 'opp-9',
    referenceNumber: 'VEN-2026-0138',
    slug: 'president-defense-business-unit',
    title: 'President, Defense Business Unit',
    status: 'verified',
    lifecycle: 'open',
    clientDisclosure: 'disclosed',
    disclosedClientName: '[Client organization name — verified, publication approved]',
    clientDescriptor: 'A tier-one aerospace and defense supplier',
    mandateId: 'mandate-7',
    industry: 'Aerospace and Defense',
    function: 'General Management',
    location: 'Washington, DC',
    region: 'united_states',
    workArrangement: 'onsite',
    employmentType: 'permanent',
    seniority: 'evp',
    compensation: {
      currency: 'USD',
      baseMin: 700000,
      baseMax: 850000,
      bonusTargetPct: 70,
      equityNote: 'Long-term incentive under the corporation’s executive plan.',
      isEstimate: false,
    },
    reportingLine: 'Reports to the Chief Executive Officer of the parent corporation',
    roleOverview:
      'Full profit-and-loss accountability for the defense business unit, including programme execution, capture strategy and the government-customer relationship.',
    organizationalContext:
      'The business unit has strong programme delivery but has under-performed on capture in recent competitions. The corporation requires a leader who can strengthen capture discipline without disturbing delivery credibility.',
    keyResponsibilities: [
      'Own the business unit profit and loss',
      'Lead capture strategy and major competition bids',
      'Hold accountability for programme execution and customer confidence',
      'Own the government-customer relationship at senior level',
      'Maintain compliance discipline across contracts and export control',
      'Develop the business unit leadership team',
    ],
    requiredQualifications: [
      'Profit-and-loss leadership of a defense business unit of comparable scale',
      'Evidenced capture leadership on major competitions',
      'Programme execution accountability on named programmes',
      'Fluency with government contracting compliance obligations',
    ],
    preferredQualifications: [
      'Space or advanced systems portfolio experience',
      'Experience integrating a defense-technology acquisition',
    ],
    leadershipScope: {
      teamSize: 'Business unit of approximately 2,500',
      budget: 'Revenue in the low billions',
      pAndLOwnership: true,
      boardExposure: true,
      directReports: 'Eight direct reports',
    },
    benefits: commonBenefits,
    requiredSkills: [
      'Business unit profit and loss leadership',
      'Capture and bid strategy',
      'Programme execution',
      'Government customer relationships',
      'Contracts compliance',
    ],
    educationRequirement: 'Bachelor’s degree required; advanced degree preferred',
    licensingRequirement:
      'The client requires eligibility for a United States government security clearance. Clearance status is discussed directly with the client; we do not collect clearance documentation or personal identifiers through this platform.',
    workAuthorizationRequirement: ['us_citizen_or_permanent_resident'],
    screeningQuestions: [
      {
        id: 'q1',
        prompt: 'Describe a major competition you led as capture lead or business-unit leader. What was the outcome and what did you change in the process?',
        type: 'long_text',
        required: true,
      },
      {
        id: 'q2',
        prompt:
          'The client requires eligibility for a United States government security clearance. Are you eligible? Do not provide clearance numbers or personal identifiers here.',
        type: 'single_select',
        options: ['Yes', 'No', 'Prefer to discuss with the consultant'],
        required: true,
      },
    ],
    applicationDeadline: '2026-11-07',
    postedAt: '2026-08-14',
    updatedAt: '2026-08-28',
    ownerRecruiterId: 'rec-1',
    internal: {
      actualClientName: '[Internal: verified client legal entity name]',
      mandateOwnerUserId: 'user-rec-1',
      verificationEvidence: 'Signed engagement letter dated 2026-08-05; corporate headcount approval recorded.',
      verifiedByUserId: 'user-admin-1',
      verificationDate: '2026-08-12',
      vacancyStatus: 'confirmed_open',
      clientAuthorizationDate: '2026-08-13',
    },
  },
  {
    id: 'opp-10',
    referenceNumber: 'VEN-2026-0141',
    slug: 'chief-development-officer-renewables',
    title: 'Chief Development Officer, Renewables',
    status: 'verified',
    lifecycle: 'open',
    clientDisclosure: 'disclosed',
    disclosedClientName: '[Client organization name — verified, publication approved]',
    clientDescriptor: 'An independent renewables development platform',
    mandateId: 'mandate-8',
    industry: 'Energy and Infrastructure',
    function: 'General Management',
    location: 'Denver, CO',
    region: 'united_states',
    workArrangement: 'hybrid',
    employmentType: 'permanent',
    seniority: 'c_suite',
    compensation: {
      currency: 'USD',
      baseMin: 450000,
      baseMax: 550000,
      bonusTargetPct: 50,
      equityNote: 'Carried interest participation in the development platform.',
      isEstimate: false,
    },
    reportingLine: 'Reports to the Chief Executive Officer',
    roleOverview:
      'Accountability for the development pipeline from origination through to notice to proceed, including interconnection strategy, land, permitting and offtake.',
    organizationalContext:
      'The platform has capital committed and a pipeline that has slowed at interconnection and permitting. The requirement is a development leader who has moved projects of comparable scale through those specific constraints, not a generalist energy executive.',
    keyResponsibilities: [
      'Own the development pipeline and its progression to notice to proceed',
      'Lead interconnection strategy and queue management',
      'Own land, permitting and community engagement',
      'Lead offtake negotiation with the commercial team',
      'Build and lead the development organization',
      'Report pipeline risk to the board and capital partners',
    ],
    requiredQualifications: [
      'Development leadership on utility-scale renewables projects taken to notice to proceed',
      'Interconnection queue and transmission constraint experience in United States markets',
      'Permitting and community engagement accountability',
      'Offtake negotiation exposure',
    ],
    preferredQualifications: ['Storage or hybrid project experience', 'Experience with more than one ISO or RTO market'],
    leadershipScope: {
      teamSize: 'Development organization of approximately 60',
      budget: 'Development capital budget in the tens of millions',
      pAndLOwnership: true,
      boardExposure: true,
      directReports: 'Five direct reports',
    },
    benefits: commonBenefits,
    requiredSkills: [
      'Utility-scale project development',
      'Interconnection and transmission',
      'Permitting and land',
      'Offtake negotiation',
      'Pipeline risk reporting',
    ],
    educationRequirement: 'Bachelor’s degree required',
    workAuthorizationRequirement: ['us_citizen_or_permanent_resident', 'authorized_no_sponsorship_required'],
    screeningQuestions: [
      {
        id: 'q1',
        prompt: 'Name the markets in which you have taken utility-scale projects to notice to proceed, and the constraints you had to resolve.',
        type: 'long_text',
        required: true,
      },
      {
        id: 'q2',
        prompt: 'Have you held accountability for an interconnection strategy across more than one ISO or RTO?',
        type: 'boolean',
        required: true,
      },
    ],
    applicationDeadline: '2026-10-20',
    postedAt: '2026-08-22',
    updatedAt: '2026-08-30',
    ownerRecruiterId: 'rec-3',
    internal: {
      actualClientName: '[Internal: verified client legal entity name]',
      mandateOwnerUserId: 'user-rec-3',
      verificationEvidence: 'Signed engagement letter dated 2026-08-15; capital partner confirmation of role and budget recorded.',
      verifiedByUserId: 'user-admin-1',
      verificationDate: '2026-08-20',
      vacancyStatus: 'confirmed_open',
      clientAuthorizationDate: '2026-08-21',
    },
  },
];

export const getOpportunity = (idOrSlug: string): Opportunity | undefined =>
  opportunities.find((o) => o.id === idOrSlug || o.slug === idOrSlug);
